<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import { store } from '@/store/appStore.js'

const brands    = computed(() => [...store.data.brandPerformance.value].sort((a,b)=>b.momGrowthPct-a.momGrowthPct))
const fast      = computed(() => brands.value.filter(b=>b.movement==='Fast').slice(0,10))
const slow      = computed(() => brands.value.filter(b=>b.movement==='Slow'))
const topChart  = computed(() => ({ labels:brands.value.slice(0,10).map(b=>b.brandName), datasets:[{ label:'MoM Growth %', data:brands.value.slice(0,10).map(b=>b.momGrowthPct), backgroundColor:brands.value.slice(0,10).map(b=>b.momGrowthPct>=0?'#3A9B5C':'#C1392B') }] }))

// BRD Module 12 asks for Territory-wise and Pharmacy-wise brand performance.
// These are derived from actual order line items (orders carry brandName and
// customerId directly), NOT from the brandPerformance.revenue figures shown
// above — that dataset is a standalone wider-business aggregate (its total
// is ~4.5x the sum of all order values in this prototype's sample) and would
// silently contradict an order-derived number for the same brand if the two
// were shown side by side without explanation. Labelled explicitly below so
// nobody mistakes one for a breakdown of the other.
const selectedSku = ref(null)
const currentSku = computed(() => selectedSku.value || brands.value[0]?.sku)
const currentBrand = computed(() => brands.value.find(b => b.sku === currentSku.value))

const brandOrders = computed(() => store.data.orders.value.filter(o => o.productSku === currentSku.value))
const orderDerivedTotal = computed(() => brandOrders.value.reduce((s,o) => s + o.orderValue, 0))

const pharmacyTerritory = computed(() => Object.fromEntries(store.data.pharmacies.value.map(p => [p.customerId, p.territory])))
const territoryBreakdown = computed(() => {
  const byTerr = {}
  brandOrders.value.forEach(o => {
    const t = pharmacyTerritory.value[o.customerId] || 'Unknown'
    byTerr[t] = byTerr[t] || { territory: t, revenue: 0, orders: 0 }
    byTerr[t].revenue += o.orderValue
    byTerr[t].orders += 1
  })
  return Object.values(byTerr).sort((a,b) => b.revenue - a.revenue)
})
const territoryChart = computed(() => ({
  labels: territoryBreakdown.value.map(t => t.territory),
  datasets: [{ label: 'Revenue (USD)', data: territoryBreakdown.value.map(t => Math.round(t.revenue)), backgroundColor: '#0F3D3E' }]
}))

const pharmacyBreakdown = computed(() => {
  const byPh = {}
  brandOrders.value.forEach(o => {
    byPh[o.customerId] = byPh[o.customerId] || { customerId: o.customerId, pharmacyName: o.pharmacyName, revenue: 0, orders: 0 }
    byPh[o.customerId].revenue += o.orderValue
    byPh[o.customerId].orders += 1
  })
  return Object.values(byPh).sort((a,b) => b.revenue - a.revenue).slice(0, 10)
})
</script>
<template>
  <div>
    <div class="card module-header"><h2>{{ brands.length }} brands tracked</h2></div>
    <div class="card" style="padding:18px 20px;margin-bottom:16px;"><div class="eyebrow" style="margin-bottom:8px;">MoM Growth % — all brands, ranked</div><ChartView type="bar" :labels="topChart.labels" :datasets="topChart.datasets" :height="260" key="brand-mom" /></div>
    <div class="split-2col" style="margin-bottom:16px;">
      <div class="card"><div class="toolbar"><span class="result-count" style="font-weight:700;color:var(--status-green);">Fast Moving</span></div>
        <div class="table-wrap"><table class="data-table"><thead><tr><th>Brand</th><th>Division</th><th>Revenue</th><th>MoM %</th></tr></thead>
          <tbody><tr v-for="b in fast" :key="b.sku"><td class="cell-strong">{{ b.brandName }}</td><td class="cell-muted">{{ b.division }}</td><td class="cell-muted">${{ b.revenue.toLocaleString() }}</td><td style="color:var(--status-green);font-weight:700;">+{{ b.momGrowthPct }}%</td></tr></tbody>
        </table></div>
      </div>
      <div class="card"><div class="toolbar"><span class="result-count" style="font-weight:700;color:var(--status-red);">Slow Moving / Inventory Aging Risk</span></div>
        <div class="table-wrap">
          <table class="data-table" v-if="slow.length"><thead><tr><th>Brand</th><th>Division</th><th>Revenue</th><th>MoM %</th></tr></thead>
            <tbody><tr v-for="b in slow" :key="b.sku"><td class="cell-strong">{{ b.brandName }}</td><td class="cell-muted">{{ b.division }}</td><td class="cell-muted">${{ b.revenue.toLocaleString() }}</td><td style="color:var(--status-red);font-weight:700;">{{ b.momGrowthPct }}%</td></tr></tbody>
          </table>
          <div v-else class="empty-state" style="padding:30px;"><p class="muted">No slow-moving brands in this scenario.</p></div>
        </div>
      </div>
    </div>

    <div class="card" style="padding:18px 20px;">
      <div class="toolbar" style="border-bottom:none;padding:0 0 12px;flex-wrap:wrap;">
        <div class="eyebrow" style="margin:0;">Territory-wise &amp; Pharmacy-wise Performance</div>
        <select class="filter-select" v-model="selectedSku">
          <option v-for="b in brands" :key="b.sku" :value="b.sku">{{ b.brandName }}</option>
        </select>
        <span class="result-count">Order-derived — {{ brandOrders.length }} order line{{ brandOrders.length===1?'':'s' }}, ${{ Math.round(orderDerivedTotal).toLocaleString() }} sample revenue</span>
      </div>
      <p class="muted" style="font-size:11.5px;margin:0 0 16px;">
        Figures below come from actual order line items for {{ currentBrand?.brandName }}, not the revenue shown in the tables above — that's a separate, wider-business aggregate. The two are not directly comparable.
      </p>

      <div v-if="!brandOrders.length" class="empty-state" style="padding:30px;">
        <p class="muted">No orders for {{ currentBrand?.brandName }} in this scenario.</p>
      </div>
      <template v-else>
        <div class="split-main-side" style="margin-bottom:20px;">
          <div>
            <div class="eyebrow" style="margin-bottom:8px;">Revenue by Territory</div>
            <ChartView type="bar" :labels="territoryChart.labels" :datasets="territoryChart.datasets" :height="240" :key="'terr-'+currentSku" />
          </div>
          <div>
            <div class="eyebrow" style="margin-bottom:8px;">Top Pharmacies</div>
            <div class="table-wrap">
              <table class="data-table">
                <thead><tr><th>Pharmacy</th><th>Revenue</th><th>Orders</th></tr></thead>
                <tbody>
                  <tr v-for="p in pharmacyBreakdown" :key="p.customerId">
                    <td class="cell-strong">{{ p.pharmacyName }}</td>
                    <td class="cell-muted">${{ Math.round(p.revenue).toLocaleString() }}</td>
                    <td class="cell-muted">{{ p.orders }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Territory</th><th>Revenue</th><th>Orders</th><th>% of Brand Total</th></tr></thead>
            <tbody>
              <tr v-for="t in territoryBreakdown" :key="t.territory">
                <td class="cell-strong">{{ t.territory }}</td>
                <td class="cell-muted">${{ Math.round(t.revenue).toLocaleString() }}</td>
                <td class="cell-muted">{{ t.orders }}</td>
                <td class="cell-muted">{{ orderDerivedTotal ? Math.round(t.revenue/orderDerivedTotal*100) : 0 }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>
